import React, { useState, useEffect, useRef } from 'react';
import { fetchWithFallback, fetchAuth, PROD_BASE } from '../../services/apiClient';
import useAuth from '../../hooks/useAuth';
import { useGlobalState } from '../../contexts/GlobalStateContext';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { GoogleMap, Marker, DirectionsRenderer, OverlayView } from '@react-google-maps/api';
import { useCurrency } from '../../contexts/CurrencyContext';
import ModalFilters from '../../components/ModalFilters/ModalFilters';
import { IoLocationOutline, IoClose, IoChevronDown, IoHomeOutline, IoFilter, IoSearch } from 'react-icons/io5';
import CurrencyDropdown from '../../components/CurrencyDropdown.jsx';
import PropertyList from '../../components/PropertyList/PropertyList';
import LocationAutocomplete from '../../components/LocationAutocomplete';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import { normalizeFavorites } from '../../utils/favorites';

const Container = styled.div`
  display: flex;
  height: calc(100vh - 80px);
  margin-top: 65px;
  
  /* Mobile: empilha mapa e lista */
  @media (max-width: 768px) {
    flex-direction: column;
    height: auto;
  }
`;

const PriceInputGroup = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #fff;
  border-radius: 8px;
`;

const CurrencyTag = styled.span`
  padding: 7px 10px;
  border: 1px solid #e5e5e5;
  border-radius: 6px;
  font-weight: 700;
  font-size: 13px;
  color: #2d3748;
  background: #f8fafc;
`;

const clampPrice = (value) => {
  const n = Number(value) || 0;
  return Math.min(100000000, Math.max(0, n));
};

const formatPriceInput = (value) => {
  const digits = String(value || '').replace(/\D/g, '');
  const num = clampPrice(digits);
  return num ? new Intl.NumberFormat('pt-BR').format(num) : '';
};

const toDigits = (value) => {
  const digits = String(value || '').replace(/\D/g, '');
  if (!digits) return '';
  return String(clampPrice(digits));
};

const DateInput = styled.input`
  padding: 8px 12px;
  border: 1px solid #e5e5e5;
  border-radius: 6px;
  width: 170px;
  font-size: 14px;
  outline: none;

  &:focus {
    border-color: #0B4F45;
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const SortSelect = styled.select`
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 10px;
  background: white;
  font-size: 0.95rem;
  color: #333;
  min-width: 160px;
  cursor: pointer;
`;

// Wrapper para posicionar o card no OverlayView e seta inferior
const BalloonWrapper = styled.div`
  position: relative;
  display: inline-block; /* width matches card, centers arrow */
  transform: translate(-50%, -100%);
  z-index: 50; /* keep above price markers */
  pointer-events: none; /* não bloquear o mapa fora do card; conteúdo interno habilita */
`;

const BalloonArrow = styled.div`
  position: absolute;
  left: 50%;
  bottom: -12px; /* sit below the rounded corner */
  transform: translateX(-50%);
  width: 0; height: 0;
  border-left: 12px solid transparent;
  border-right: 12px solid transparent;
  border-top: 12px solid #fff; /* same color as InfoCard */
  filter: drop-shadow(0 -1px 2px rgba(0,0,0,0.12));
`;

const CurrencyWrapper = styled.div`
  min-width: 150px; /* aumenta um pouco a largura do seletor de moedas */

  @media (max-width: 768px) {
    display: none;
  }
`;

const CurrencyWrapperMobile = styled.div`
  display: none;
  width: 100%;

  @media (max-width: 768px) {
    display: block;
  }
`;

const MapWrapper = styled.div`
  flex: 1;
  position: relative;
  height: 100%;
  background: #e5e7eb; /* fundo neutro enquanto o mapa carrega */

  /* Altura fixa para mapa no mobile */
  @media (max-width: 768px) {
    height: 320px;
  }
`;

const ListWrapper = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  background-color: #f9f9f9;
  
  @media (max-width: 768px) {
    overflow-y: visible;
    padding: 12px;
  }
`;

const TopBar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 16px;
  background: white;
  padding: 12px 20px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
`;

const TopBarRow = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: nowrap; /* mantém fixa 1 linha por row no desktop */
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
    flex-wrap: wrap; /* mobile pode quebrar */
  }
`;

const SearchTypeGroup = styled.div`
  display: flex;
  gap: 4px;
  padding: 4px;
  background: #f5f5f5;
  border-radius: 8px;
  flex-wrap: wrap;
`;

const SearchTypeButton = styled.button`
  padding: 8px 16px;
  background: ${props => props.$active ? '#0B4F45' : 'transparent'};
  color: ${props => props.$active ? 'white' : '#333'};
  border: none;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 14px;

  &:hover {
    background: ${props => props.$active ? '#0B4F45' : '#e5e5e5'};
  }
`;


const PriceInput = styled.input`
  padding: 8px 12px;
  border: 1px solid #e5e5e5;
  border-radius: 6px;
  width: 140px;
  font-size: 14px;
  outline: none;

  &:focus {
    border-color: #0B4F45;
  }

  &::placeholder {
    color: #999;
  }
  
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const LocationInputWrapper = styled.div`
  position: relative;
  flex: 1;
  display: flex;
  align-items: center;
  min-width: 240px;
  
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const LocationIcon = styled(IoLocationOutline)`
  position: absolute;
  left: 12px;
  color: #666;
  font-size: 18px;
`;

const ClearButton = styled(IoClose)`
  position: absolute;
  right: 12px;
  color: #666;
  font-size: 18px;
  cursor: pointer;
  
  &:hover {
    color: #333;
  }
`;

const LocationInput = styled.input`
  padding: 8px 36px;
  border: 1px solid #e5e5e5;
  border-radius: 6px;
  width: 100%;
  font-size: 14px;
  outline: none;

  &:focus {
    border-color: #0B4F45;
  }

  &::placeholder {
    color: #999;
  }
`;

const PropertyTypeDropdown = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #e5e5e5;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  max-height: 300px;
  overflow-y: auto;
  /* Evita que o scroll "vaze" para a página quando chega ao topo/fundo */
  overscroll-behavior: contain;
`;

const DropdownOption = styled.div`
  padding: 12px 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  color: ${props => props.selected ? '#0B4F45' : '#333'};
  background: ${props => props.selected ? '#f5f5f5' : 'white'};
  font-weight: ${props => props.selected ? '500' : 'normal'};

  &:first-child {
    border-radius: 6px 6px 0 0;
    background: #C4845C;
    color: white;
    font-weight: 500;
  }

  &:not(:first-child):hover {
    background: #f5f5f5;
  }
`;

const PropertyTypeSelect = styled.div`
  position: relative;
  padding: 8px 32px 8px 12px;
  border: 1px solid #e5e5e5;
  border-radius: 6px;
  cursor: pointer;
  min-width: 180px;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
  background: white;

  &:hover {
    border-color: #0B4F45;
  }
  
  @media (max-width: 768px) {
    width: 100%;
    min-width: 0;
  }
`;

const ChevronIcon = styled(IoChevronDown)`
  position: absolute;
  right: 8px;
  color: #666;
  font-size: 16px;
`;

// Botão icônico compacto para ações (Filtros/Pesquisar)
const IconButton = styled.button`
  padding: 8px;
  background: white;
  border: 1px solid #e5e5e5;
  border-radius: 6px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #333;
  width: 36px;
  height: 36px;

  &:hover {
    border-color: #0B4F45;
    color: #0B4F45;
  }
`;

// Cartão estilizado do InfoWindow do mapa
const InfoCard = styled.div`
  width: 280px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  pointer-events: auto; /* card clicável */
`;

const InfoImageWrap = styled.div`
  position: relative;
  width: 100%;
  height: 160px;
  overflow: hidden;
`;

const InfoImage = styled.img`
  display: block;
  width: 100%;
  height: 160px;
  object-fit: cover;
`;

const ImageOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, rgba(0,0,0,0) 40%, rgba(0,0,0,0.45) 100%);
`;

const ImageTopBar = styled.div`
  position: absolute;
  top: 8px;
  left: 8px;
  right: 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  z-index: 1;
`;

const ImageTags = styled.div`
  display: flex;
  gap: 6px;
`;

const InfoTag = styled.span`
  padding: 6px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  color: #fff;
  background: rgba(0,0,0,0.5);
  backdrop-filter: saturate(140%);
`;

const InfoTypeTag = styled(InfoTag)`
  background: ${props => {
    const type = (props.$type || '').toLowerCase();
    if (type === 'comprar') return '#0B4F45';
    if (type === 'arrendar' || type === 'rent') return '#2C88D9';
    return '#8E44AD';
  }};
`;

const CloseFab = styled.button`
  border: none;
  width: 28px;
  height: 28px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(0,0,0,0.55);
  color: #fff;
  cursor: pointer;
  transition: background .2s ease;

  &:hover { background: rgba(0,0,0,0.75); }
`;

const InfoBody = styled.div`
  padding: 12px 14px 8px 14px;
`;

const InfoTitle = styled.h3`
  margin: 0 0 6px 0;
  font-size: 16px;
  font-weight: 700;
  color: #1d2939;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const InfoLocation = styled.p`
  margin: 0;
  font-size: 12px;
  color: #667085;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const InfoFooter = styled.div`
  padding: 10px 14px 14px 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`;

const PriceBadge = styled.div`
  background: #0B4F45;
  color: #fff;
  padding: 8px 10px;
  border-radius: 10px;
  font-weight: 700;
  font-size: 14px;
  white-space: nowrap;
`;

const RouteButton = styled.button`
  background: #C4845C;
  color: #fff;
  border: none;
  border-radius: 10px;
  padding: 8px 10px;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
  transition: background 0.2s ease;
  white-space: nowrap;

  &:hover {
    background: #b37346;
  }
`;

// Marcador de preço (estilo Airbnb)
const PriceMarker = styled.button`
  border: 1px solid rgba(0,0,0,0.15);
  background: ${props => props.$active ? '#0B4F45' : '#fff'};
  color: ${props => props.$active ? '#fff' : '#111827'};
  font-weight: 800;
  font-size: 12px;
  line-height: 1;
  padding: 8px 10px;
  border-radius: 999px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  cursor: pointer;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  user-select: none;
  transition: transform .15s ease, background .15s ease, color .15s ease, box-shadow .15s ease;
  pointer-events: auto; /* botão recebe cliques */

  &:hover {
    transform: translateY(-6px) scale(1.06);
    box-shadow: 0 8px 20px rgba(0,0,0,0.2);
  }
`;

const PriceMarkerWrapper = styled.div`
  position: relative;
  transform: translate(-50%, -100%);
  z-index: 1;
  pointer-events: none; /* apenas o botão interno interage */
`;

const SearchResults = () => {
  const { t, i18n } = useTranslation();
  const { isLoggedIn, user } = useAuth();
  const globalState = useGlobalState();
  const handleOpenLogin = globalState?.handleOpenLogin || (() => {});
  const { currency, currencySymbol, exchangeRate } = useCurrency();
  const [favorites, setFavorites] = useState([]);

  const sortProperties = (list, sortBy) => {
    const sorted = [...list];
    if (sortBy === 'priceAsc') {
      sorted.sort((a, b) => (Number(a.price) || 0) - (Number(b.price) || 0));
    } else if (sortBy === 'priceDesc') {
      sorted.sort((a, b) => (Number(b.price) || 0) - (Number(a.price) || 0));
    } else {
      sorted.sort((a, b) => {
        const dateDifference = new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
        return dateDifference || (Number(b.id) || 0) - (Number(a.id) || 0);
      });
    }
    return sorted;
  };

  // Buscar favoritos do usuário ao carregar
  useEffect(() => {
    const fetchFavorites = async () => {
      if (!isLoggedIn || !user) return;
      const token = localStorage.getItem('auth_token');
      try {
        const res = await fetchWithFallback(`/favoritos/usuario/${user.codigo || user.id || user.userId}`,
          {
            headers: {
              'accept': 'application/json',
              'Authorization': `Bearer ${token}`
            }
          });
        const data = await res.json();
        setFavorites(normalizeFavorites(data));
      } catch {
        setFavorites([]);
      }
    };
    fetchFavorites();
  }, [isLoggedIn, user]);

  // Favoritar
  const handleFavorite = async (property) => {
    if (!isLoggedIn) {
      handleOpenLogin();
      return;
    }
    try {
      const token = localStorage.getItem('auth_token');
      const payload = {
        imovelFAVORITO: property.id,
        usuarioFAVORITO: user?.codigo || user?.id || user?.userId,
        ativoFAVORITO: 1
      };
      const response = await fetchWithFallback(`/favoritos`, {
        method: 'POST',
        headers: {
          'accept': 'application/json',
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });
      if (response.ok) {
        // Atualize favoritos após favoritar
        const res = await fetchWithFallback(`/favoritos/usuario/${payload.usuarioFAVORITO}`, {
          headers: {
            'accept': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        });
        const data = await res.json();
        setFavorites(normalizeFavorites(data));
        window.dispatchEvent(new Event('favoritesChanged'));
        try {
          const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;
          if (isMobile) {
            Swal.fire({
              icon: 'success',
              title: 'Imóvel adicionado aos favoritos',
              confirmButtonText: 'OK',
              width: '90%',
              timer: 2000,
              timerProgressBar: true,
            });
          } else {
            Swal.fire({
              toast: true,
              position: 'top-end',
              icon: 'success',
              title: 'Adicionado aos favoritos',
              showConfirmButton: false,
              timer: 2000,
            });
          }
        } catch (_) {
          // Ignore errors
        }
      } else {
        try {
          Swal.fire({
            icon: 'error',
            title: 'Erro ao favoritar',
            text: 'Tente novamente em instantes.',
            width: '90%',
          });
        } catch (_) {
          // Ignore errors
        }
      }
    } catch (err) {
      try {
        Swal.fire({
          icon: 'error',
          title: 'Erro ao favoritar',
          text: 'Tente novamente em instantes.',
          width: '90%',
        });
      } catch (_) {
        // Ignore errors
      }
    }
  };

  // Desfavoritar
  const handleUnfavorite = async (property) => {
    if (!isLoggedIn) {
      handleOpenLogin();
      return;
    }
    try {
      const token = localStorage.getItem('auth_token');
      // Descubra o codigoFAVORITO pelo imovel
      const fav = favorites.find(f => String(f.imovelFAVORITO) === String(property.id));
      if (!fav) return;
      await fetchWithFallback(`/favoritos/${fav.codigoFAVORITO}`, {
        method: 'DELETE',
        headers: {
          'accept': '*/*',
          'Authorization': `Bearer ${token}`
        }
      });
      // Atualize favoritos após remover
      const res = await fetchWithFallback(`/favoritos/usuario/${user.codigo || user.id || user.userId}`, {
        headers: {
          'accept': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await res.json();
      setFavorites(normalizeFavorites(data));
      window.dispatchEvent(new Event('favoritesChanged'));
      try {
        const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;
        if (isMobile) {
          Swal.fire({
            icon: 'info',
            title: 'Imóvel removido dos favoritos',
            confirmButtonText: 'OK',
            width: '90%',
            timer: 2000,
            timerProgressBar: true,
          });
        } else {
          Swal.fire({
            toast: true,
            position: 'top-end',
            icon: 'info',
            title: 'Removido dos favoritos',
            showConfirmButton: false,
            timer: 2000,
          });
        }
      } catch (_) {
        // Ignore errors
      }
    } catch (err) {
      try {
        Swal.fire({
          icon: 'error',
          title: 'Erro ao remover favorito',
          text: 'Tente novamente em instantes.',
          width: '90%',
        });
      } catch (_) {
        // Ignore errors
      }
    }
  };


  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const initialSearchType = (() => {
    const rawParam = (searchParams.get('searchType') || '').toLowerCase();
    const tipoParam = searchParams.get('tipo');
    const labelValorParam = (searchParams.get('labelvalor') || '').toLowerCase();

    if (rawParam === 'comprar' || rawParam === 'buy') return 'buy';
    if (rawParam === 'arrendar' || rawParam === 'rent') return 'rent';
    if (rawParam === 'férias' || rawParam === 'ferias' || rawParam === 'vacation') return 'vacation';

    // Mapeia código numérico de tipo (backend) quando presente
    if (tipoParam === '2') return 'rent';
    if (tipoParam === '3') return 'vacation';
    if (tipoParam === '1') return 'buy';

    // labelvalor=ferias também deve forçar vacation
    if (labelValorParam.includes('ferias') || labelValorParam.includes('férias') || labelValorParam.includes('vacation')) return 'vacation';

    // Fallback pelo pathname
    const path = (location.pathname || '').toLowerCase();
    if (path.includes('arrendar') || path.includes('rent')) return 'rent';
    if (path.includes('ferias') || path.includes('férias') || path.includes('vacation')) return 'vacation';
    return 'buy';
  })();
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [priceTouched, setPriceTouched] = useState(false);
  const [priceFromUrl, setPriceFromUrl] = useState(false);
  const [dateStart, setDateStart] = useState('');
  const [dateEnd, setDateEnd] = useState('');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [searchType, setSearchType] = useState(initialSearchType);
  const [locationSearch, setLocationSearch] = useState('');
  const [locationTouched, setLocationTouched] = useState(false);
  const [propertyType, setPropertyType] = useState('Categorias');
  const [isPropertyTypeOpen, setIsPropertyTypeOpen] = useState(false);

  useEffect(() => {
    if (isFilterOpen) setIsPropertyTypeOpen(false);
  }, [isFilterOpen]);

  const [selectedFilters, setSelectedFilters] = useState([]);
  const [selectedBedrooms, setSelectedBedrooms] = useState(0);
  const [selectedBathrooms, setSelectedBathrooms] = useState(0);
  const [selectedParking, setSelectedParking] = useState(0);
  const [sortOption, setSortOption] = useState('recent'); // recent | priceAsc | priceDesc
  // Visualização e paginação no servidor
  const [viewMode, setViewMode] = useState('list');
  const [serverPage, setServerPage] = useState(1);
  const [serverTotalPages, setServerTotalPages] = useState(1);

  // Exibe 8 anúncios por página em qualquer modo de visualização
  const pageSize = 8;
  const [userLocation, setUserLocation] = useState(null);
  // Centro neutro inicial (Atlântico) para evitar forçar Brasil quando não há país/localização
  const [mapCenter, setMapCenter] = useState({ lat: 20, lng: 0 });
  const [zoom, setZoom] = useState(5); // um pouco mais próximo para evitar aparência de "bloco cinza"
  const [directions, setDirections] = useState(null);
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const [map, setMap] = useState(null);
  const [properties, setProperties] = useState([]);
  const [filteredProperties, setFilteredProperties] = useState([]); // <-- Adicionado corretamente aqui
  const [allProperties, setAllProperties] = useState([]); // resultados completos para paginação local
  const [loading, setLoading] = useState(true);
  // Mantém últimos resultados não vazios para não "apagar" a área
  const lastNonEmptyPropertiesRef = useRef([]);
  useEffect(() => {
    if (Array.isArray(filteredProperties) && filteredProperties.length > 0) {
      lastNonEmptyPropertiesRef.current = filteredProperties;
    }
  }, [filteredProperties]);
  const [categories, setCategories] = useState([]); // Estado para armazenar as categorias
  const [minArea, setMinArea] = useState(0); // metragem mínima (número)
  const [maxArea, setMaxArea] = useState(1000); // metragem máxima (número)
  const [premiumOnly, setPremiumOnly] = useState(false); // filtrar somente imóveis em destaque
  const dropdownRef = useRef(null);
  const [situacao, setSituacao] = useState(''); // Novo | Usado | Em Construção
  // Comodidades booleanas
  const [amenities, setAmenities] = useState({});
  // Chave para forçar refetch quando page não muda (ex.: já está na página 1)
  const [queryKey, setQueryKey] = useState(0);
  const forceReload = () => setQueryKey((k) => k + 1);
  // Evita ressincronizar estados a partir da URL em toda mudança e causar loops/flicker
  const didInitFromUrl = useRef(false);
  // Guarda a última query construída para evitar requisições duplicadas
  const lastQueryRef = useRef('');

  const handleDropdownWheel = (e) => {
    const el = dropdownRef.current;
    if (!el) return;
    const atTop = el.scrollTop <= 0;
    const atBottom = Math.ceil(el.scrollTop + el.clientHeight) >= el.scrollHeight;
    // Se tentar rolar para além do topo/fundo, evita rolar a página e fecha o menu
    if ((atTop && e.deltaY < 0) || (atBottom && e.deltaY > 0)) {
      e.preventDefault();
      e.stopPropagation();
      setIsPropertyTypeOpen(false);
    } else {
      // Impede propagação para que a página não role enquanto navega na lista
      e.stopPropagation();
    }
  };

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const userPos = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };
          setUserLocation(userPos);
          setMapCenter(userPos);
          setZoom(12);
        },
        (error) => {
          console.error('Erro ao obter localização:', error);
        }
      );
    }
  }, []);

  // UI loading: mostra imediatamente ao iniciar e atrasa o esconder para evitar "piscas"
  const [uiLoading, setUiLoading] = useState(false);
  useEffect(() => {
    let t;
    if (loading) {
      setUiLoading(true);
    } else {
      // pequena espera para não sumir instantaneamente
      t = setTimeout(() => setUiLoading(false), 150);
    }
    return () => t && clearTimeout(t);
  }, [loading]);

  // Inicializa datas e faixa de preço/localização a partir da URL (roda uma vez, antes de categorias)
  useEffect(() => {
    const dateStartParam = searchParams.get('dateStart') || searchParams.get('dataInicio') || '';
    const dateEndParam = searchParams.get('dateEnd') || searchParams.get('dataFim') || '';
    if (dateStartParam) setDateStart(dateStartParam);
    if (dateEndParam) setDateEnd(dateEndParam);

    const minPriceParam = searchParams.get('minPrice') || searchParams.get('valorMin') || '';
    const maxPriceParam = searchParams.get('maxPrice') || searchParams.get('valorMax') || '';
    if (minPriceParam) setMinPrice(formatPriceInput(minPriceParam));
    if (maxPriceParam) setMaxPrice(formatPriceInput(maxPriceParam));

    const locationParam = searchParams.get('location') || '';
    if (locationParam) setLocationSearch((prev) => prev || locationParam);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const queryUrl = buildQuery(1, 1000);
    if (queryUrl === lastQueryRef.current) {
      // Evita refetch se nada mudou na query
      return;
    }
    lastQueryRef.current = queryUrl;
    const fetchPaged = async () => {
      setLoading(true);
      const { country } = parseLocationTarget();
      try {
        let res = await fetchWithFallback(queryUrl);
        console.log('[SearchResults] Fetch response:', {
          url: queryUrl,
          status: res.status,
          ok: res.ok
        });
        if (!res.ok) throw new Error(`Primary fetch failed: ${res.status}`);
        let text = await res.text();
        let json;
        try { json = text ? JSON.parse(text) : []; } catch (_) {
          throw new Error('Primary response is not JSON');
        }

        const list = Array.isArray(json?.data) ? json.data : (Array.isArray(json) ? json : []);
        const pg = json?.pagination || {};
        const total = pg?.total || list.length;
        
        console.log('[SearchResults] Resposta do backend:', {
          url: queryUrl,
          status: res.status,
          totalItems: total,
          items: list.slice(0, 3).map(item => ({
            id: item.codigoIMOVEL,
            titulo: item.tituloIMOVEL,
            city: item.cityIMOVEL,
            tipo: item.tipoIMOVEL,
            valor: item.valorIMOVEL
          }))
        });

        // Confia no filtro ativo=1 enviado ao backend; filtro local causava páginas vazias/inconsistentes
        const sanitizedList = list;

        const formatted = sanitizedList.map((item) => {
          const typeCodeResolved = Number(item?.tipoIMOVEL ?? item?.categoria?.tipo?.codigoTipo ?? item?.categoria?.tipo?.codigoTIPOIMOVEL ?? item?.categoria?.tipo);
          const backendTypeName = String(item?.categoria?.tipo?.nome || '').toLowerCase();
          const labelValorRaw = String(item?.labelvalorIMOVEL ?? item?.labelValor ?? item?.labelvalor ?? '').toLowerCase();
          const labelValorTag = labelValorRaw.includes('férias') || labelValorRaw.includes('ferias') || labelValorRaw.includes('temporada') || labelValorRaw.includes('vacation') ? 'ferias' : '';
          const normalizedTypeTag = labelValorTag || (Number.isFinite(typeCodeResolved)
            ? (typeCodeResolved === 3 ? 'ferias' : (typeCodeResolved === 2 ? 'arrendar' : 'comprar'))
            : backendTypeName.includes('comprar')
              ? 'comprar'
              : (backendTypeName.includes('arrendar') || backendTypeName.includes('rent'))
                ? 'arrendar'
                : (backendTypeName.includes('férias') || backendTypeName.includes('ferias') || backendTypeName.includes('vacation'))
                  ? 'ferias'
                  : ((searchType === 'buy') ? 'comprar' : (searchType === 'rent') ? 'arrendar' : 'ferias'));

          return {
          id: item.codigoIMOVEL,
          createdAt: item.datacriacaoIMOVEL,
          title: item.tituloIMOVEL,
          location: item.enderecoIMOVEL,
          slug: item.slugIMOVEL || item.slug || item.slugimovel || item.SLUG || '',
          // Trata preços como "12.215,33" ou "1800.00"
          price: (() => {
            const s = String(item.valorIMOVEL ?? '').trim();
            if (!s) return 0;
            const normalized = /,\d{2}$/.test(s) ? s.replace(/\./g, '').replace(',', '.') : s;
            const n = Number(normalized);
            return Number.isFinite(n) ? n : 0;
          })(),
          rating: 0,
          image: item.fotos?.[0]?.urlFOTO || '',
          coordinates: {
            lat: Number(item.latitudeIMOVEL) || 0,
            lng: Number(item.longitudeIMOVEL) || 0,
          },
          garagem: item.vagasgaragemIMOVEL,
          banheiros: item.banheirosIMOVEL,
          quartos: item.quartosIMOVEL,
          type: item.tipoIMOVEL,
          category: item.categoriaIMOVEL,
          categoryName: item?.categoria?.nomeCATEGORIA || '',
          typeName: item?.categoria?.tipo?.nome || '',
          searchType: normalizedTypeTag,
          featured: item.destaqueIMOVEL === 1 || item.isDestaque === true,
          metragem: item.metragemIMOVEL,
          country: item.paisIMOVEL || item.countryIMOVEL || item.country || '',
          city: item.cityIMOVEL || item.cidadeIMOVEL || item.cidade || '',
          situacao: item.situacaoIMOVEL || item.situacao || item.estadoIMOVEL || '',
        }});

        // Busca todos os resultados e faz paginação local para garantir 8 itens por página
        const sorted = sortProperties(formatted, sortOption);
        console.log('[SearchResults] Itens formatados:', formatted.length);
        console.log('[SearchResults] searchType atual:', searchType);

        setAllProperties(sorted);
      } catch (error) {
        console.error('Error fetching paged properties:', error);
        // tentativa de fallback sem localização para evitar 500
        try {
          const params = new URLSearchParams();
          params.set('page', '1');
          params.set('pageSize', '1000');
          params.set('ativo', '1');
          const tipoCode = searchType === 'buy' ? 1 : searchType === 'rent' ? 2 : 3;
          params.set('tipo', String(tipoCode));
          if (premiumOnly) params.set('destaque', '1');
          if (searchType === 'vacation' || searchType === 'ferias') params.set('labelvalor', 'ferias');
          if (country) params.set('pais', country);
          const fallbackUrl = `/imoveis?${params.toString()}`;
          const res = await fetchWithFallback(fallbackUrl);
          if (res.ok) {
            const text = await res.text();
            let json = [];
            try { json = text ? JSON.parse(text) : []; } catch (_) {
              // Ignore JSON parse errors
            }
            const list = Array.isArray(json?.data) ? json.data : (Array.isArray(json) ? json : []);
            // Confia no filtro ativo=1 enviado ao backend; filtro local causava páginas vazias/inconsistentes
            const sanitizedList = list;

            const formatted = sanitizedList.map((item) => {
              const typeCodeResolved = Number(item?.tipoIMOVEL ?? item?.categoria?.tipo?.codigoTipo ?? item?.categoria?.tipo?.codigoTIPOIMOVEL ?? item?.categoria?.tipo);
              const backendTypeName = String(item?.categoria?.tipo?.nome || '').toLowerCase();
              const labelValorRaw = String(item?.labelvalorIMOVEL ?? item?.labelValor ?? item?.labelvalor ?? '').toLowerCase();
              const labelValorTag = labelValorRaw.includes('férias') || labelValorRaw.includes('ferias') || labelValorRaw.includes('temporada') || labelValorRaw.includes('vacation') ? 'ferias' : '';
              const normalizedTypeTag = labelValorTag || (Number.isFinite(typeCodeResolved)
                ? (typeCodeResolved === 3 ? 'ferias' : (typeCodeResolved === 2 ? 'arrendar' : 'comprar'))
                : backendTypeName.includes('comprar')
                  ? 'comprar'
                  : (backendTypeName.includes('arrendar') || backendTypeName.includes('rent'))
                    ? 'arrendar'
                    : (backendTypeName.includes('férias') || backendTypeName.includes('ferias') || backendTypeName.includes('vacation'))
                      ? 'ferias'
                      : 'unknown');

              return {
              id: item.codigoIMOVEL,
              title: item.tituloIMOVEL,
              location: item.enderecoIMOVEL,
              slug: item.slugIMOVEL || item.slug || item.slugimovel || item.SLUG || '',
              price: (() => {
                const s = String(item.valorIMOVEL ?? '').trim();
                if (!s) return 0;
                const normalized = /,\d{2}$/.test(s) ? s.replace(/\./g, '').replace(',', '.') : s;
                const n = Number(normalized);
                return Number.isFinite(n) ? n : 0;
              })(),
              rating: 0,
              image: item.fotos?.[0]?.urlFOTO || '',
              coordinates: {
                lat: Number(item.latitudeIMOVEL) || 0,
                lng: Number(item.longitudeIMOVEL) || 0,
              },
              garagem: item.vagasgaragemIMOVEL,
              banheiros: item.banheirosIMOVEL,
              quartos: item.quartosIMOVEL,
              type: item.tipoIMOVEL,
              category: item.categoriaIMOVEL,
              categoryName: item?.categoria?.nomeCATEGORIA || '',
              typeName: item?.categoria?.tipo?.nome || '',
              searchType: normalizedTypeTag,
              featured: item.destaqueIMOVEL === 1 || item.isDestaque === true,
              metragem: item.metragemIMOVEL,
              country: item.paisIMOVEL || item.countryIMOVEL || item.country || '',
              city: item.cityIMOVEL || item.cidadeIMOVEL || item.cidade || '',
              situacao: item.situacaoIMOVEL || item.situacao || item.estadoIMOVEL || '',
            }});

            const sorted = sortProperties(formatted, sortOption);
            setAllProperties(sorted);
          }
        } catch (err2) {
          console.error('Fallback fetch failed:', err2);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchPaged();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    premiumOnly,
    searchParams,
    queryKey,
    sortOption,
  ]);

  // Paginação local: garante exatamente 8 itens por página (exceto a última, se houver menos)
  useEffect(() => {
    const totalItems = allProperties.length;
    const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
    setServerTotalPages(totalPages);
    const validPage = Math.max(1, Math.min(serverPage, totalPages));
    const start = (validPage - 1) * pageSize;
    const pageItems = allProperties.slice(start, start + pageSize);
    setProperties(pageItems);
    setFilteredProperties(pageItems);
    if (validPage !== serverPage) setServerPage(validPage);
  }, [allProperties, serverPage, pageSize]);

  const extractCategories = (payload) => {
    if (Array.isArray(payload)) return payload;
    if (payload && typeof payload === 'object') {
      const candidates = [payload.data, payload.categorias, payload.results];
      for (const c of candidates) {
        if (Array.isArray(c) && c.length) return c;
      }
      // tenta pegar o primeiro valor que seja array
      const firstArray = Object.values(payload).find((v) => Array.isArray(v));
      if (Array.isArray(firstArray)) return firstArray;
    }
    return [];
  };

  const isActiveCategory = (cat = {}) => {
    const flag = cat.ativoCATEGORIA ?? cat.ativo ?? cat.active ?? cat.status ?? cat.statusCATEGORIA ?? cat.statusCategoria;
    if (flag === undefined || flag === null) return false;
    const v = String(flag).trim().toLowerCase();
    if (v === 'true' || v === '1' || v === 'ativo' || v === 'active') return true;
    if (Number(flag) === 1) return true;
    return false;
    };

  const fetchCategories = async () => {
    const tipoCode = searchType === 'buy' ? 1 : (searchType === 'rent' ? 2 : 3);
    const envBase = String(import.meta.env?.VITE_URL_API || '').replace(/\/?$/, '');
    const paths = [
      `/categorias?tipo=${tipoCode}&ativo=1`,
      `/categorias?ativo=1`,
      envBase ? `${envBase}/categorias?tipo=${tipoCode}&ativo=1` : null,
      envBase ? `${envBase}/categorias?ativo=1` : null,
      `${PROD_BASE}/categorias?tipo=${tipoCode}&ativo=1`
    ].filter(Boolean);

    for (const path of paths) {
      try {
        // Tenta com auth; se falhar, tenta sem auth
        const attempt = async () => {
          const token = localStorage.getItem('auth_token');
          if (token) {
            const { data, res } = await fetchAuth(path);
            if (res && !res.ok) throw new Error(`Erro ao buscar categorias: ${res.status}`);
            return data;
          }
          const res = await fetchWithFallback(path);
          if (!res.ok) throw new Error(`Erro ao buscar categorias: ${res.status}`);
          return res.json();
        };

        let data = await attempt();
        if (typeof data === 'string') {
          try { data = JSON.parse(data); } catch (_) {
            // Ignore JSON parse errors
          }
        }
        const extracted = extractCategories(data).filter(isActiveCategory);
        console.debug('Categorias recebidas:', extracted?.length ?? 0, 'via', path);
        if (extracted.length) {
          setCategories(extracted);
          return;
        }
        // se não vier nada, continua tentando próximo path
      } catch (error) {
        console.error('Erro ao buscar categorias:', error);
      }
    }

    // fallback final
    // mantém o que já tinha para não zerar dropdown se algo falhar
    setCategories((prev) => Array.isArray(prev) && prev.length ? prev : []);
  };

  useEffect(() => {
    fetchCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchType]);

  // Normalizador para comparação textual sem acentos e case-insensitive
  const normalize = (str) => (str || '')
    .toString()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

  const filteredCategories = (() => {
    const filtered = categories.filter((category) => {
      const tipoBackend = category?.tipoCATEGORIA ?? category?.tipoCategoria ?? category?.tipo ?? category?.tipoCategoria?.nome;
      const tipoNumber = Number(tipoBackend);
      const tipoText = normalize(tipoBackend);
      const targetType = searchType === 'buy' ? 1 : searchType === 'rent' ? 2 : 3;

      // Match numérico direto
      if (Number.isFinite(tipoNumber) && tipoNumber === targetType) return true;

      // Match textual (ex.: "comprar", "arrendar", "ferias")
      if (tipoText) {
        if (targetType === 1 && tipoText.includes('compr')) return true;
        if (targetType === 2 && (tipoText.includes('arrend') || tipoText.includes('rent'))) return true;
        if (targetType === 3 && (tipoText.includes('feria') || tipoText.includes('vacation'))) return true;
      }

      // Se o tipo não veio, não bloquear exibição (fallback)
      return !tipoBackend;
    });

    return filtered.length ? filtered : categories;
  })();

  const translateCategoryLabel = (cat = {}) => {
    const slug = String(cat.slugCATEGORIA || cat.slug || '').toLowerCase();
    const rawName = String(cat.nomeCATEGORIA || cat.nome || '').toLowerCase();
    const norm = (s) => s.normalize('NFD').replace(/\p{Diacritic}/gu, '').replace(/\s+/g, '-').replace(/_/g, '-');

    const slugNorm = norm(slug);
    const nameNorm = norm(rawName);
    const slugKey = `category.types.${slugNorm}`;
    const nameKey = `category.types.${nameNorm}`;

    const fromSlug = t(slugKey, { defaultValue: slugKey });
    if (fromSlug !== slugKey) return fromSlug;
    const fromName = t(nameKey, { defaultValue: nameKey });
    if (fromName !== nameKey) return fromName;

    const lang = (i18n?.language || '').split('-')[0];
    const dict = {
      fr: {
        'apart-hotel': 'Appart-hôtel',
        'apartamentos': 'Appartements',
        'armazens': 'Entrepôts',
        'condominios': 'Copropriétés',
        'hoteis': 'Hôtels',
        'moradias': 'Maisons',
        'palacios-castelos': 'Palais / Châteaux',
        'predios': 'Immeubles',
        'predios-comerciais-coworking': 'Immeubles commerciaux / Coworking',
        'salas-coworking': 'Salles / Coworking',
        'lojas': 'Magasins',
        'garagens': 'Garages',
        'quintas-e-herdades': 'Domaines / Fermes',
        'terrenos': 'Terrains',
      },
      en: {
        'apart-hotel': 'Aparthotel',
        'apartamentos': 'Apartments',
        'armazens': 'Warehouses',
        'condominios': 'Condominiums',
        'hoteis': 'Hotels',
        'moradias': 'Houses',
        'palacios-castelos': 'Palaces / Castles',
        'predios': 'Buildings',
        'predios-comerciais-coworking': 'Commercial buildings / Coworking',
        'salas-coworking': 'Offices / Coworking',
        'lojas': 'Stores',
        'garagens': 'Garages',
        'quintas-e-herdades': 'Farms / Estates',
        'terrenos': 'Land',
      },
      es: {
        'apart-hotel': 'Apart-hotel',
        'apartamentos': 'Apartamentos',
        'armazens': 'Almacenes',
        'condominios': 'Condominios',
        'hoteis': 'Hoteles',
        'moradias': 'Casas',
        'palacios-castelos': 'Palacios / Castillos',
        'predios': 'Edificios',
        'predios-comerciais-coworking': 'Edificios comerciales / Coworking',
        'salas-coworking': 'Oficinas / Coworking',
        'lojas': 'Tiendas',
        'garagens': 'Garajes',
        'quintas-e-herdades': 'Fincas / Haciendas',
        'terrenos': 'Terrenos',
      },
    };

    const dictVal = dict[lang]?.[slugNorm] || dict[lang]?.[nameNorm];
    return dictVal || cat.nomeCATEGORIA || cat.nome || cat.slugCATEGORIA || slug || rawName;
  };

  const parseLocationTarget = () => {
    const urlCountry = (searchParams.get('country') || '').trim();
    const q = (locationSearch || searchParams.get('location') || '').trim();
    const parts = q.split(',').map(p => p.trim()).filter(Boolean);
    // Se o usuário digitou apenas um termo (ex.: "Portugal"), trate como país
    if (!urlCountry && parts.length === 1) {
      return {
        city: '',
        state: '',
        country: parts[0] || ''
      };
    }
    return {
      city: parts[0] || '',
      state: parts.length >= 2 ? parts[parts.length - 2] : '',
      country: urlCountry || (parts.length ? parts[parts.length - 1] : '')
    };
  };

  // Monta query para busca paginada no servidor
  const buildQuery = (pageOverride, pageSizeOverride) => {
    const params = new URLSearchParams();
    params.set('page', String(pageOverride ?? serverPage ?? 1));
    params.set('pageSize', String(pageSizeOverride ?? pageSize ?? 8));
    // Retornar apenas imóveis ativos
    params.set('ativo', '1');
    const urlCountry = (searchParams.get('country') || '').trim();
    const q = (locationSearch || searchParams.get('location') || '').trim();
    if (q) {
      // Detecta formato "Cidade - Estado" ou "Cidade, Estado, País"
      let cityGuess = '';
      let stateGuess = '';
      let countryGuess = '';
      
      // Primeiro tenta separar por hífen (ex: "Jarinu - SP")
      if (q.includes(' - ')) {
        const dashParts = q.split(' - ').map(p => p.trim()).filter(Boolean);
        cityGuess = dashParts[0] || '';
        stateGuess = dashParts[1] || '';
        // Se houver vírgula após estado, pode ser país
        if (stateGuess.includes(',')) {
          const stateParts = stateGuess.split(',').map(p => p.trim()).filter(Boolean);
          stateGuess = stateParts[0] || '';
          countryGuess = stateParts[1] || '';
        }
      } else {
        // Senão, separa por vírgula (ex: "Jarinu, SP, Brasil")
        const parts = q.split(',').map(p => p.trim()).filter(Boolean);
        cityGuess = parts[0] || '';
        stateGuess = parts.length >= 2 ? parts[1] : '';
        countryGuess = parts.length >= 3 ? parts[2] : '';
        // Heurística: se há apenas um termo (ex.: "Portugal"), considere como país
        if (parts.length === 1) {
          countryGuess = cityGuess;
          cityGuess = '';
          stateGuess = '';
        }
      }
      
      const normalizeCountryParam = (val) => {
        const raw = (val || '').trim();
        if (!raw) return '';
        // Se for sigla (<=3), envia em maiúsculas (ex.: pt -> PT). Se for nome, mantém.
        return raw.length <= 3 ? raw.toUpperCase() : raw;
      };

      if (cityGuess) params.set('city', cityGuess);
      if (stateGuess) params.set('state', stateGuess);
      if (countryGuess) params.set('country', normalizeCountryParam(countryGuess));
    }
    if (urlCountry) {
      const normalizeCountryParam = (val) => {
        const raw = (val || '').trim();
        if (!raw) return '';
        return raw.length <= 3 ? raw.toUpperCase() : raw;
      };
      params.set('country', normalizeCountryParam(urlCountry));
    }
    if (premiumOnly) params.set('destaque', '1');
    // Filtros do modal -> API
    // selectedFilters contém itens { id, kind: 'category' | 'type' }
    const categorias = (selectedFilters || []).filter(f => f.kind === 'category').map(f => f.id);
    const tipos = (selectedFilters || []).filter(f => f.kind === 'type').map(f => f.id);
    if (categorias.length) params.set('categoria', categorias.join(','));

    const isVacation = searchType === 'vacation' || searchType === 'ferias';
    if (isVacation) {
      // Sempre força tipo=3 e labelvalor=ferias no modo férias, mesmo que algum filtro de tipo tenha sido selecionado
      params.set('tipo', '3');
      params.set('labelvalor', 'ferias');
    } else if (tipos.length) {
      params.set('tipo', tipos.join(','));
    } else {
      // Se nenhum tipo foi selecionado explicitamente, usa a aba ativa (mapeamento explícito)
      const tipoMapFromState = { buy: 1, comprar: 1, rent: 2, arrendar: 2, vacation: 3, ferias: 3 };
      const tipoCode = tipoMapFromState[searchType] ?? 3;
      params.set('tipo', String(tipoCode));
    }
    // Quantitativos
    if (Number(selectedBedrooms) > 0) params.set('quartos', String(selectedBedrooms));
    if (Number(selectedBathrooms) > 0) params.set('banheiros', String(selectedBathrooms));
    if (Number(selectedParking) > 0) params.set('vagasgaragem', String(selectedParking));
    // Situação (se preenchida). Backend mapeia chaves genéricas para *_IMOVEL
    if (situacao) params.set('situacao', String(situacao));
    // Preço: backend filtra por texto em 'valor'; enviaremos range textual se ambos preenchidos, senão enviaremos o que houver
    // Sempre verifica URL primeiro para garantir que parâmetros sejam enviados
    const urlMinPrice = searchParams.get('minPrice') || searchParams.get('valorMin') || '';
    const urlMaxPrice = searchParams.get('maxPrice') || searchParams.get('valorMax') || '';
    const urlValor = searchParams.get('valor') || '';
    
    const minPriceDigits = toDigits(minPrice) || urlMinPrice;
    const maxPriceDigits = toDigits(maxPrice) || urlMaxPrice;
    const shouldSendPrice = priceTouched || priceFromUrl || urlMinPrice || urlMaxPrice || urlValor;
    console.log('[SearchResults:buildQuery] Price state:', { minPrice, maxPrice, urlMinPrice, urlMaxPrice, minPriceDigits, maxPriceDigits, priceTouched, priceFromUrl, shouldSendPrice });
    if (shouldSendPrice) {
      if (minPriceDigits && maxPriceDigits) params.set('valor', `${minPriceDigits}-${maxPriceDigits}`);
      else if (minPriceDigits) params.set('valor', String(minPriceDigits));
      else if (maxPriceDigits) params.set('valor', String(maxPriceDigits));
      else if (urlValor) params.set('valor', urlValor);
      console.log('[SearchResults:buildQuery] Sending valor:', params.get('valor'));
    } else {
      console.log('[SearchResults:buildQuery] NOT sending valor (shouldSendPrice=false)');
    }
    // Período (férias): manter datas quando disponíveis
    if (searchType === 'vacation') {
      if (dateStart) params.set('dateStart', dateStart);
      if (dateEnd) params.set('dateEnd', dateEnd);
    }
    // Comodidades booleanas: envia como =1 quando selecionado
    Object.entries(amenities || {}).forEach(([key, val]) => {
      if (val) params.set(key, '1');
    });
    // Metragem: enviar quando usuário ajusta range (numérico). Usaremos formato textual min-max.
    if ((Number(minArea) > 0) || (Number(maxArea) < 1000)) {
      const min = Math.max(0, Number(minArea) || 0);
      const max = Math.max(min + 1, Number(maxArea) || 0);
      params.set('metragem', `${min}-${max}`);
    }
    // A ordenação deve ser aplicada no backend antes da paginação e também no
    // frontend para manter a ordem ao trocar entre as opções.
    if (sortOption === 'priceAsc' || sortOption === 'priceDesc') {
      params.set('orderBy', 'valor');
      params.set('orderDirection', sortOption === 'priceAsc' ? 'ASC' : 'DESC');
    } else {
      params.set('orderBy', 'datacriacao');
      params.set('orderDirection', 'DESC');
    }
    return `/imoveis?${params.toString()}`;
  };

  // Log para depurar a query
  const queryUrl = buildQuery();
  console.log('[SearchResults] Query URL:', queryUrl);

  // Opções do dropdown (sempre categorias do modo atual)
  const dropdownOptions = (() => {
    const cats = filteredCategories.map(c => ({
      id: c.codigoCATEGORIA ?? c.codigoCategoria ?? c.id ?? c.codigo,
      label: translateCategoryLabel(c),
      kind: 'category'
    }));
    const seen = new Set();
    const opts = cats.filter(opt => {
      const key = String(opt.label || '').trim().toLowerCase();
      if (!key) return false;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    if (!opts.length) {
      return [{ id: 'none', label: 'Nenhuma categoria encontrada', kind: 'category', disabled: true }];
    }
    return opts;
  })();

  const handlePropertyTypeSelect = (opt) => {
    setPropertyType(opt.label);
    setIsPropertyTypeOpen(false);
    if (opt && opt.kind === 'category') {
      setSelectedFilters([{ id: opt.id, kind: 'category' }]);
      // Recarrega resultados com a categoria aplicada
      setServerPage(1);
      forceReload();
    }
  };

  // Inicializa a partir da URL apenas uma vez, mas esperando as categorias filtradas estarem disponíveis
  useEffect(() => {
    if (didInitFromUrl.current) return;

    const categoriesSource = (filteredCategories && filteredCategories.length)
      ? filteredCategories
      : (categories && categories.length ? categories : []);

    if (!categoriesSource.length) return;

    const targetTypeCode = searchType === 'buy' ? 1 : searchType === 'rent' ? 2 : 3;
    const categoriesForType = categoriesSource.filter((cat) => {
      const tipoBackend = cat?.tipoCATEGORIA ?? cat?.tipoCategoria ?? cat?.tipo;
      return Number(tipoBackend) === Number(targetTypeCode);
    });
    const matchPool = categoriesForType.length ? categoriesForType : categoriesSource;

    const locationParam = searchParams.get('location') || '';
    const valorParam = searchParams.get('valor') || '';
    const minPriceParam = searchParams.get('minPrice') || searchParams.get('valorMin') || '';
    const maxPriceParam = searchParams.get('maxPrice') || searchParams.get('valorMax') || '';
    const dateStartParam = searchParams.get('dateStart') || searchParams.get('dataInicio') || '';
    const dateEndParam = searchParams.get('dateEnd') || searchParams.get('dataFim') || '';
    const propertyTypeParam = searchParams.get('propertyType')
      || searchParams.get('categoria')
      || searchParams.get('category')
      || 'Categorias';
    const rawSearchTypeParam = (searchParams.get('searchType') || '').toLowerCase();
    const premiumParam = searchParams.get('premium') === '1';

    console.log('[SearchResults] searchType da URL:', rawSearchTypeParam);
    console.log('[SearchResults] searchType atual:', searchType);

    const mappedType = (
      rawSearchTypeParam === 'comprar' ? 'buy' :
      rawSearchTypeParam === 'arrendar' || rawSearchTypeParam === 'rent' ? 'rent' :
      rawSearchTypeParam === 'férias' || rawSearchTypeParam === 'ferias' || rawSearchTypeParam === 'vacation' ? 'vacation' :
      null
    );

    console.log('[SearchResults] mappedType:', mappedType);

    // Se a URL indicar um tipo diferente do estado atual, primeiro ajusta o searchType
    // e aguarda o re-fetch de categorias para esse tipo, evitando marcar init cedo demais.
    if (mappedType && searchType !== mappedType) {
      setSearchType(mappedType);
      return;
    }

    if (locationParam) setLocationSearch((prev) => prev || locationParam);

    // Debug: inspeciona o que estamos recebendo para casar categoria
    console.debug('[SearchResults:initFromUrl] propertyTypeParam =', propertyTypeParam);
    console.debug('[SearchResults:initFromUrl] filteredCategories =', (matchPool || []).map(c => ({
      id: c.codigoCATEGORIA ?? c.codigoCategoria ?? c.id ?? c.codigo,
      nome: c.nomeCATEGORIA || c.nomeCategoria || c.nome,
      slug: c.slugCATEGORIA || c.slugCategoria || c.slug,
    })));

    const normalizeLight = (str) => String(str || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[-_]+/g, ' ')
      .toLowerCase()
      .trim();

    if (propertyTypeParam && propertyTypeParam !== 'Categorias') {
      const lowered = normalizeLight(propertyTypeParam);
      const match = (matchPool || []).find((cat) => {
        const slug = cat.slugCATEGORIA || cat.slugCategoria || cat.slug;
        const name = cat.nomeCATEGORIA || cat.nomeCategoria || cat.nome;
        return (
          normalizeLight(slug) === lowered ||
          normalizeLight(name) === lowered
        );
      });

      if (match) {
        const id = match.codigoCATEGORIA ?? match.codigoCategoria ?? match.id ?? match.codigo;
        const label = match.nomeCATEGORIA || match.nomeCategoria || match.nome || propertyTypeParam;
        setPropertyType(label);
        const nextFilters = [{ id: String(id), kind: 'category' }];
        console.debug('[SearchResults:initFromUrl] matched category ->', { id, label, nextFilters });
        setSelectedFilters(nextFilters);
      } else {
        // Se não localizar na lista, mantém só o texto para não quebrar a UI.
        setPropertyType(propertyTypeParam);
        setSelectedFilters([]);
      }
    } else {
      // Sem propertyType específico: reseta seleção de categoria.
      setSelectedFilters([]);
      setPropertyType('Categorias');
    }

    if (mappedType) setSearchType(mappedType);
    setPremiumOnly(Boolean(premiumParam));

    // Preço (mantém valores vindos da URL, como na home)
    let nextMinPrice = '';
    let nextMaxPrice = '';
    if (valorParam && valorParam.includes('-')) {
      const [minStr, maxStr] = valorParam.split('-');
      nextMinPrice = minStr || '';
      nextMaxPrice = maxStr || '';
    } else if (valorParam) {
      nextMinPrice = valorParam;
    }
    if (minPriceParam) nextMinPrice = minPriceParam;
    if (maxPriceParam) nextMaxPrice = maxPriceParam;

    const hasPriceParams = Boolean(valorParam || minPriceParam || maxPriceParam);
    console.log('[SearchResults:initFromUrl] hasPriceParams:', hasPriceParams, { valorParam, minPriceParam, maxPriceParam });
    console.log('[SearchResults:initFromUrl] nextMinPrice:', nextMinPrice, 'nextMaxPrice:', nextMaxPrice);
    setPriceFromUrl(hasPriceParams);
    if (hasPriceParams) {
      setPriceTouched(true);
      console.log('[SearchResults:initFromUrl] setPriceTouched(true)');
    }
    setMinPrice(formatPriceInput(nextMinPrice));
    setMaxPrice(formatPriceInput(nextMaxPrice));
    if (dateStartParam) setDateStart(dateStartParam);
    if (dateEndParam) setDateEnd(dateEndParam);

    setFilteredProperties(properties);
    if (!didInitFromUrl.current) {
      didInitFromUrl.current = true;
      forceReload();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, properties, filteredCategories, categories]);

  useEffect(() => {
    setFilteredProperties(properties);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [properties]);

  // Atualiza URL quando preço é alterado
  useEffect(() => {
    if (!priceTouched && !priceFromUrl) return;
    
    const params = new URLSearchParams(searchParams);
    const minDigits = toDigits(minPrice);
    const maxDigits = toDigits(maxPrice);
    
    if (minDigits || maxDigits) {
      if (minDigits) params.set('minPrice', minDigits);
      else params.delete('minPrice');
      
      if (maxDigits) params.set('maxPrice', maxDigits);
      else params.delete('maxPrice');
    } else {
      params.delete('minPrice');
      params.delete('maxPrice');
      params.delete('valor');
    }
    
    navigate({ search: params.toString() }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minPrice, maxPrice, priceTouched, priceFromUrl]);

  useEffect(() => {
    if (isFilterOpen) {
      const match = dropdownOptions.find((cat) => cat.id === selectedFilters[0]?.id);
      if (match) {
        setPropertyType(match.label);
      }
    }
  }, [isFilterOpen, selectedFilters, dropdownOptions]);

  const applyFilters = () => {
    // Com paginação e filtros no servidor, apenas resetamos para a página 1
    setServerPage(1);
  };

  // ao abrir a página com ?location=... ou ?country=..., centraliza o mapa
  useEffect(() => {
    const urlLocation = searchParams.get('location');
    const urlCountry = searchParams.get('country');
    if (!locationTouched) {
      if (urlLocation) {
        // Inicializa a partir da URL apenas se o usuário ainda não editou
        setLocationSearch(urlLocation);
        geocodeAddress(urlLocation);
      } else if (urlCountry) {
        geocodeAddress(urlCountry);
      }
    }
  }, [searchParams, locationTouched]);

  const handleSearchTypeChange = (type) => {
    // Muda o modo e força nova busca no servidor
    setSearchType(type);
    // Limpa categoria selecionada ao trocar de modo
    setPropertyType('Categorias');
    setSelectedFilters([]);
    // Reseta para página 1 para disparar nova requisição
    setServerPage(1);
    // Persiste o modo na URL para manter contexto ao voltar do detalhe
    const params = new URLSearchParams(searchParams);
    params.set('searchType', type);
    if (type === 'vacation') {
      params.set('tipo', '3');
      params.set('labelvalor', 'ferias');
    } else if (type === 'rent') {
      params.set('tipo', '2');
      params.delete('labelvalor');
    } else {
      params.set('tipo', '1');
      params.delete('labelvalor');
    }
    navigate({ search: params.toString() }, { replace: true });
    forceReload();
  };

  const handleLocationSelect = (suggestion) => {
    setLocationSearch(suggestion.description);
    const geocoder = new window.google.maps.Geocoder();

    geocoder.geocode({ placeId: suggestion.id }, (results, status) => {
      if (status === 'OK' && results[0]) {
        const location = results[0].geometry.location;
        const newCenter = {
          lat: location.lat(),
          lng: location.lng(),
        };
        setMapCenter(newCenter);
        setZoom(12);
        setDirections(null);
        setSelectedProperty(null);
      }
    });
  };

  // Geocodifica um endereço digitado ou vindo da URL (quando não há seleção da sugestão)
  const geocodeAddress = (address) => {
    if (!address) return;
    if (!window.google || !window.google.maps) return;
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ address }, (results, status) => {
    if (status === 'OK' && results[0]) {
      const loc = results[0].geometry.location;
      const newCenter = { lat: loc.lat(), lng: loc.lng() };
      setMapCenter(newCenter);
      setZoom(12);
    }
  });
};

// Ajusta automaticamente o mapa para os imóveis filtrados quando não há país/localização selecionado
  useEffect(() => {
    if (!isMapLoaded || !map) return;
    if (filteredProperties && filteredProperties.length > 0) {
      const bounds = new window.google.maps.LatLngBounds();
      filteredProperties.forEach((p) => {
        if (p?.coordinates?.lat && p?.coordinates?.lng) {
          bounds.extend(p.coordinates);
        }
      });
      // Só aplica se bounds forem válidos
      if (!bounds.isEmpty()) {
        map.fitBounds(bounds);
        // Em telas pequenas, garante um zoom mínimo para o usuário ver detalhes
        const isMobile = window.innerWidth <= 768;
        if (isMobile) {
          const listener = window.google.maps.event.addListenerOnce(map, 'bounds_changed', () => {
            if (map.getZoom() > 12) {
              map.setZoom(12);
            }
          });
          return () => window.google.maps.event.removeListener(listener);
        }
      }
    }
  }, [isMapLoaded, map, filteredProperties]);

  const onMapLoad = (mapInstance) => {
    setIsMapLoaded(true);
    setMap(mapInstance);
    console.log('Mapa carregado com sucesso:', mapInstance);

    // Em alguns cenários (especialmente mobile/devtools responsivo),
    // o mapa inicializa antes do container ter o tamanho final.
    // Disparamos um "resize" e recentralizamos para forçar os tiles a renderizarem.
    try {
      const isMobile = window.innerWidth <= 768;
      if (window.google && window.google.maps && mapInstance) {
        window.google.maps.event.trigger(mapInstance, 'resize');
        // Reaplica centro/zoom atual após pequeno atraso
        setTimeout(() => {
          try {
            mapInstance.setCenter(mapCenter);
            mapInstance.setZoom(isMobile ? Math.max(zoom, 6) : zoom);
          } catch {
            // Ignore map errors
          }
        }, 150);
      }
    } catch {
      // Ignore geocoding errors
    }
  };

  const handlePropertyClick = (property) => {
    if (!isMapLoaded) return;

    setSelectedProperty(property);
    if (userLocation) {
      const directionsService = new window.google.maps.DirectionsService();
      directionsService.route(
        {
          origin: userLocation,
          destination: property.coordinates,
          travelMode: window.google.maps.TravelMode.DRIVING,
        },
        (result, status) => {
          if (status === 'OK') {
            setDirections(result);
          }
        }
      );
    }
  };

  const handleFiltersApply = () => {
    setIsFilterOpen(false);
    applyFilters();
    forceReload();
  };

  // Lista já vem paginada do servidor
  // Durante carregamento, mantém os últimos resultados não vazios para evitar branco total
  const currentProperties = uiLoading && lastNonEmptyPropertiesRef.current.length > 0
    ? lastNonEmptyPropertiesRef.current
    : filteredProperties;
  
  console.log('[SearchResults] currentProperties:', {
    uiLoading,
    lastNonEmptyLength: lastNonEmptyPropertiesRef.current.length,
    filteredPropertiesLength: filteredProperties.length,
    currentPropertiesLength: currentProperties.length,
    currentProperties: currentProperties.slice(0, 2).map(p => ({ id: p.id, title: p.title }))
  });
  
  const totalPages = serverTotalPages;

  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    setServerPage(1);
  };

  const handlePageChange = (pageNumber) => {
    setServerPage(pageNumber);
    window.scrollTo(0, 0);
  };

  const formatPriceForInfo = (val, type) => {
    const converted = Math.round(((Number(val) || 0) * exchangeRate + Number.EPSILON) * 100) / 100;
    const formatted = converted.toLocaleString('pt-BR');
    let suffix = '';
    switch ((type || '').toLowerCase()) {
      case 'comprar':
      case 'buy':
      case 'venda':
        suffix = '';
        break;
      case 'arrendar':
      case 'rent':
      case 'aluguel':
        suffix = ' /mês';
        break;
      case 'ferias':
      case 'férias':
      case 'vacation':
        suffix = ' /noite';
        break;
      default:
        suffix = '';
        break;
    }
    return `${currencySymbol || currency} ${formatted}${suffix}`;
  };

  const formatPriceCompact = (val) => {
    const converted = Math.round(((Number(val) || 0) * exchangeRate + Number.EPSILON) * 100) / 100;
    const formatted = converted.toLocaleString('pt-BR');
    return `${currencySymbol || currency} ${formatted}`;
  };

  const buildPropertyUrl = (p) => {
    const base = `/property/${p.id}`;
    const s = (p.slug || '').toString().trim();
    return s ? `${base}/${encodeURIComponent(s)}` : base;
  };

  const handleViewDetails = (property) => {
    try {
      navigate(buildPropertyUrl(property));
    } catch (_) {
      window.location.href = buildPropertyUrl(property);
    }
  };

  const propertyInfoWindow = (property) => (
    <InfoCard>
      {property.image && (
        <InfoImageWrap>
          <ImageTopBar>
            <ImageTags>
              {!!property.categoryName && (
                <InfoTag>{property.categoryName}</InfoTag>
              )}
              <InfoTypeTag $type={(property.searchType || '').toLowerCase()}>
                {(property.searchType || '').toLowerCase() === 'comprar' ? 'Comprar' : (property.searchType || '').toLowerCase() === 'arrendar' ? 'Arrendar' : 'Férias'}
              </InfoTypeTag>
            </ImageTags>
            <CloseFab onClick={() => { setSelectedProperty(null); setDirections(null); }} aria-label="Fechar">
              <IoClose size={16} />
            </CloseFab>
          </ImageTopBar>
          <InfoImage src={property.image} alt={property.title} />
          <ImageOverlay />
        </InfoImageWrap>
      )}
      <InfoBody>
        <InfoTitle>{property.title}</InfoTitle>
        <InfoLocation>{property.location}</InfoLocation>
      </InfoBody>
      <InfoFooter>
        <PriceBadge>
          {property.exibirPreco === false || property.exibirPreco === 0 || property.exibirPreco === '0' ? (
            <>
              <div style={{ whiteSpace: 'nowrap' }}>Sob consulta</div>
              <div style={{ fontSize: '10px', color: '#666', marginTop: '2px' }}>
                Entre em contato para saber o valor
              </div>
            </>
          ) : (
            formatPriceForInfo(property.price, property.searchType)
          )}
        </PriceBadge>
        <div style={{ display: 'flex', gap: 8 }}>
          <RouteButton onClick={() => handleViewDetails(property)}>
            Ver detalhes
          </RouteButton>
          {userLocation && (
            <RouteButton onClick={() => handlePropertyClick(property)}>
              Ver rota
            </RouteButton>
          )}
        </div>
      </InfoFooter>
    </InfoCard>
  );

  return (
    <Container>
      <MapWrapper>
        <GoogleMap
          mapContainerStyle={{ width: '100%', height: '100%' }}
          center={mapCenter}
          zoom={zoom}
          onLoad={onMapLoad}
            onError={(error) => console.error('Erro ao renderizar o mapa:', error)}
          >
            {isMapLoaded && userLocation && (
              <Marker
                position={userLocation}
                icon={{
                  url: 'https://maps.google.com/mapfiles/ms/icons/blue-dot.png',
                  scaledSize: new window.google.maps.Size(40, 40),
                }}
                title="Sua localização"
              />
            )}
            
            {isMapLoaded && currentProperties.map((property) => (
              <OverlayView
                key={property.id}
                position={property.coordinates}
                mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
              >
                <PriceMarkerWrapper>
                  <PriceMarker
                    $active={selectedProperty && selectedProperty.id === property.id}
                    onClick={() => setSelectedProperty(property)}
                    title={property.title}
                  >
                    {property.exibirPreco === false || property.exibirPreco === 0 || property.exibirPreco === '0' ? 'Sob consulta' : formatPriceCompact(property.price)}
                  </PriceMarker>
                </PriceMarkerWrapper>
              </OverlayView>
            ))}

            {isMapLoaded && selectedProperty && (
              <OverlayView
                position={selectedProperty.coordinates}
                mapPaneName={OverlayView.FLOAT_PANE}
              >
                <BalloonWrapper>
                  {propertyInfoWindow(selectedProperty)}
                  <BalloonArrow />
                </BalloonWrapper>
              </OverlayView>
            )}

            {isMapLoaded && directions && (
              <DirectionsRenderer directions={directions} />
            )}
          </GoogleMap>

      </MapWrapper>

      <ListWrapper>
        <TopBar>
          {/* Linha 1: Abas + Categorias + Moeda + Preços */}
          <TopBarRow>
            <SearchTypeGroup>
              <SearchTypeButton 
                $active={searchType === 'buy'} 
                onClick={() => handleSearchTypeChange('buy')}
              >
                {t('searchResults.tabs.buy', { defaultValue: 'Comprar' })}
              </SearchTypeButton>
              <SearchTypeButton 
                $active={searchType === 'rent'} 
                onClick={() => handleSearchTypeChange('rent')}
              >
                {t('searchResults.tabs.rent', { defaultValue: 'Arrendar' })}
              </SearchTypeButton>
              <SearchTypeButton 
                $active={searchType === 'vacation'} 
                onClick={() => handleSearchTypeChange('vacation')}
              >
                {t('searchResults.tabs.vacation', { defaultValue: 'Férias' })}
              </SearchTypeButton>
            </SearchTypeGroup>

            {/* Seletor de Categorias */}
            <PropertyTypeSelect onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (isFilterOpen) return;
              setIsPropertyTypeOpen(!isPropertyTypeOpen);
            }}>
              <IoHomeOutline size={16} />
              {propertyType || t('searchResults.category.all', { defaultValue: 'Categorias' })}
              <ChevronIcon />
              {isPropertyTypeOpen && (
                <PropertyTypeDropdown ref={dropdownRef} onWheel={handleDropdownWheel}>
                  <DropdownOption
                    selected={propertyType === t('searchResults.category.all', { defaultValue: 'See all' })}
                    onClick={(e) => {
                      e.stopPropagation();
                      const allLabel = t('searchResults.category.all', { defaultValue: 'See all' });
                      setPropertyType(allLabel);
                      setIsPropertyTypeOpen(false);
                      setSelectedFilters([]);
                      // Recarrega sem categoria
                      setServerPage(1);
                      forceReload();
                    }}
                  >
                    {t('searchResults.category.all', { defaultValue: 'See all' })}
                  </DropdownOption>
                  {/* Lista somente de categorias do modo atual */}
                  {/* Separador visual */}
                  <div style={{ height: 1, background: '#e5e5e5', margin: '6px 0' }} />
                  {dropdownOptions
                    .filter(o => o.kind === 'category')
                    .map(opt => (
                      <DropdownOption
                        key={`${opt.kind}-${opt.id}`}
                        selected={propertyType === opt.label}
                        onClick={(e) => { e.stopPropagation(); handlePropertyTypeSelect(opt); setIsPropertyTypeOpen(false); }}
                      >
                        {opt.label}
                      </DropdownOption>
                  ))}
                </PropertyTypeDropdown>
              )}
            </PropertyTypeSelect>
            {/* Moeda apenas na primeira linha (desktop) */}
            <CurrencyWrapper>
              <CurrencyDropdown />
            </CurrencyWrapper>
          </TopBarRow>
          {/* Linha 2: Localização (menor) + ícones no fim */}
          <TopBarRow style={{ flexWrap: 'wrap' }}>
            <LocationInputWrapper style={{ flex: '1 1 360px', maxWidth: 460 }}>
              <LocationIcon />
              <LocationAutocomplete
                value={locationSearch}
                onChange={(v) => { setLocationTouched(true); setLocationSearch(v); }}
                onLocationSelect={handleLocationSelect}
                placeholder={t('searchResults.search.locationPlaceholder', { defaultValue: 'Digite uma localização...' })}
              />
              {locationSearch && (
                <ClearButton onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setLocationTouched(true);
                  setLocationSearch('');
                }} />
              )}
            </LocationInputWrapper>

            {searchType === 'vacation' && (
              <>
                <DateInput
                  type="date"
                  value={dateStart}
                  onChange={(e) => setDateStart(e.target.value)}
                  title={t('searchResults.search.dateStart', { defaultValue: 'Data inicial' })}
                />
                <span style={{ fontWeight: 600 }}>→</span>
                <DateInput
                  type="date"
                  value={dateEnd}
                  onChange={(e) => setDateEnd(e.target.value)}
                  title={t('searchResults.search.dateEnd', { defaultValue: 'Data final' })}
                />
              </>
            )}

            {/* Preço mínimo e máximo na segunda linha */}
            <PriceInputGroup>
              <CurrencyTag>{currency || 'EUR'}</CurrencyTag>
              <PriceInput 
                type="text"
                inputMode="numeric"
                placeholder={t('searchResults.search.minPrice', { defaultValue: '85.000,00' })}
                value={minPrice}
                onChange={(e) => { setMinPrice(formatPriceInput(e.target.value)); setPriceTouched(true); }}
              />
            </PriceInputGroup>
            <span style={{ fontWeight: 600 }}>→</span>
            <PriceInputGroup>
              <CurrencyTag>{currency || 'EUR'}</CurrencyTag>
              <PriceInput 
                type="text"
                inputMode="numeric"
                placeholder={t('searchResults.search.maxPrice', { defaultValue: '125.000,00' })}
                value={maxPrice}
                onChange={(e) => { setMaxPrice(formatPriceInput(e.target.value)); setPriceTouched(true); }}
              />
            </PriceInputGroup>

            {/* Moeda na linha de preço apenas no mobile */}
            <CurrencyWrapperMobile>
              <CurrencyDropdown />
            </CurrencyWrapperMobile>

            <SortSelect value={sortOption} onChange={(e) => {
              const val = e.target.value;
              setSortOption(val);
              setFilteredProperties((prev) => sortProperties(prev, val));
            }}>
              <option value="recent">Mais recentes</option>
              <option value="priceAsc">Menor preço</option>
              <option value="priceDesc">Maior preço</option>
            </SortSelect>

            {/* Ícones compactos no final */}
            <IconButton onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsPropertyTypeOpen(false);
              setIsFilterOpen(true);
            }} title={t('common.filters', { defaultValue: 'Filtros' })} aria-label="Filtros">
              <IoFilter size={18} />
            </IconButton>
            <IconButton onClick={handleFiltersApply} title={t('searchResults.search.button', { defaultValue: 'Pesquisar' })} aria-label="Pesquisar" style={{ background: '#0B4F45', color: 'white', borderColor: '#0B4F45' }}>
              <IoSearch size={18} />
            </IconButton>
          </TopBarRow>
        </TopBar>

        {!uiLoading && filteredProperties.length === 0 && (
          <div style={{
            background: 'white',
            borderRadius: 8,
            padding: 24,
            boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
            marginBottom: 16,
            display: 'flex',
            flexDirection: 'column',
            gap: 12
          }}>
            <div style={{fontSize: 16, color: '#333', fontWeight: 600}}>
              {t('searchResults.empty.title', { defaultValue: 'Sem imóveis neste local de momento — mas temos centenas de opções prontas para si em todo o mundo.' })}
            </div>
            <button
              type="button"
              onClick={() => {
                try {
                  navigate('/');
                } catch (_) {
                  window.location.href = '/';
                }
                // Garante estar no topo ao chegar na Home
                setTimeout(() => {
                  try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch (_) {
                    // Ignore scroll errors
                  }
                }, 50);
              }}
              style={{
                alignSelf: 'flex-start',
                background: '#0B4F45',
                color: 'white',
                border: 'none',
                borderRadius: 6,
                padding: '10px 16px',
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              {t('searchResults.empty.newSearch', { defaultValue: 'Nova Pesquisa' })}
            </button>
          </div>
        )}

        <ModalFilters
          isOpen={isFilterOpen}
          onClose={() => setIsFilterOpen(false)}
          selectedTypes={selectedFilters}
          setSelectedTypes={setSelectedFilters}
          selectedLabel={propertyType}
          typeOptions={dropdownOptions}
          typeOptionsTitle={searchType === 'vacation' ? 'Categorias' : 'Tipos de imóvel'}
          quartos={selectedBedrooms}
          setQuartos={setSelectedBedrooms}
          banheiros={selectedBathrooms}
          setBanheiros={setSelectedBathrooms}
          garagem={selectedParking}
          setGaragem={setSelectedParking}
          searchTerm={locationSearch}
          setSearchTerm={setLocationSearch}
          minArea={minArea}
          setMinArea={setMinArea}
          maxArea={maxArea}
          setMaxArea={setMaxArea}
          minPrice={minPrice}
          setMinPrice={setMinPrice}
          maxPrice={maxPrice}
          setMaxPrice={setMaxPrice}
          situacaoValue={situacao}
          onSituacaoChange={setSituacao}
          amenities={amenities}
          setAmenities={setAmenities}
          searchType={searchType}
          onClear={() => {
            setSelectedFilters([]);
            setSelectedBedrooms(0);
            setSelectedBathrooms(0);
            setSelectedParking(0);
            setMinArea(0);
            setMaxArea(1000);
            setMinPrice('');
            setMaxPrice('');
            setSituacao('');
            setAmenities({});
          }}
          onApply={handleFiltersApply}
        />

        <PropertyList 
          properties={currentProperties}
          currentPage={serverPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          onPropertyClick={handlePropertyClick}
          isFavorited={id => favorites.some(fav => String(fav.imovelFAVORITO) === String(id))}
          onFavorite={handleFavorite}
          onUnfavorite={handleUnfavorite}
          loading={uiLoading}
          viewMode={viewMode}
          onViewModeChange={handleViewModeChange}
          setAmenities={setAmenities}
        />
      </ListWrapper>
    </Container>
  );
};

export default SearchResults;